import { useState } from 'react';

import TABLE_CONSTANTS from '@constants/admin/tables';
import { Row } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import CreateProductModal from '@/components/admin/CreateProductModal';
import ResizableTable from '@/components/admin/ResizableTable';
import SectionHeading from '@/components/admin/SectionHeading';
import TextInput from '@/components/admin/TextInput';
import { ErrorTypeEnum } from '@/enums/error-type.enum';
import { useGetProductsQuery, Product, SortEnumType } from '@/graphql/main';
import usePaginate from '@/hooks/admin/usePaginate';
import useError from '@/hooks/error/useError';
import useColDefs from '@/pages/admin/CommercialAgreements/Products/hooks/useColDefs';

const ProductsTable = () => {
    const {
        handlePageChange,
        pageSize,
        itemsToSkip,
        setTotalItems,
        totalItems,
        sortingOrder,
        setSortingOrder
    } = usePaginate();
    const { setError, clearError } = useError();
    const [tableData, setTableData] = useState<Product[]>([]);
    const { t } = useTranslation('translation');
    const [searchText, setSearchText] = useState('');
    const { TableSize } = TABLE_CONSTANTS;

    const { loading, refetch } = useGetProductsQuery({
        variables: {
            searchText,
            take: pageSize,
            skip: itemsToSkip,
            order: sortingOrder
        },
        onCompleted: (data) => {
            if (data?.products?.items) {
                const nodes = data.products.items.map((item) => item).filter(Boolean) as Product[];
                setTableData(nodes);
                setTotalItems(data.products.totalCount);
            }
            clearError();
        },
        onError: (error) => {
            setError(error, ErrorTypeEnum.ApolloError);
        }
    });

    const { colDef } = useColDefs({
        onChangeSuccess: async () => await refetch()
    });
    const onSortChange = (sorterResult: SorterResult<Product>) =>
        setSortingOrder(
            sorterResult.order
                ? {
                      [sorterResult.columnKey as string]:
                          sorterResult.order === 'ascend' ? SortEnumType.Asc : SortEnumType.Desc
                  }
                : undefined
        );

    return (
        <>
            <SectionHeading heading={t('PRODUCTS_TABLE_TITLE')} />

            <Row justify={'space-between'} align={'bottom'} style={{ marginBottom: 18 }}>
                <TextInput inputValue={searchText} setInputValue={setSearchText} />
                <CreateProductModal successCallback={refetch} />
            </Row>

            <ResizableTable
                colDefs={colDef}
                tableData={tableData}
                emptyText={t('NO_RESULTS')}
                loading={loading}
                onPageChange={handlePageChange}
                totalPaginationItems={totalItems}
                size={TableSize}
                onChange={(_, __, sorterResult) =>
                    onSortChange(sorterResult as SorterResult<Product>)
                }
            />
        </>
    );
};

export default ProductsTable;
